<template>
  <form @submit.prevent class="space-y-4">
    <div class="flex items-baseline -mx-2 group justify-between">
      <div class="flex-none px-2 uppercase">
        <label for="orderName">ονομα:</label>
      </div>
      <div class="w-2/3 px-2">
        <ValidationProvider
          name="name"
          vid="name"
          rules="required"
          v-slot="{ errors, failed }"
          tag="div"
        >
          <input
            id="orderName"
            type="text"
            v-model.lazy="orderName"
            :readonly="!$membershipHasPermissionTo('edit order items')"
            :disabled="submitting"
            :class="{
              'bg-transparent': !failed,
              'border-transparent': !failed,
              'focus:bg-gray-400': !failed,
              'group-hover:border-gray-400': !failed,
              'bg-red-200': failed,
              'text-red-700': failed,
              'border-red-200': failed,
              'group-hover:border-red-500': failed,
              'focus:bg-red-500': failed,
              'focus:border-red-500': failed,
              'focus:text-white': failed,
            }"
            class="text-right w-full rounded px-4 py-2 leading-normal border outline-none bg-gray-200"
            placeholder="Order #1"
          />
          <p v-if="failed" class="msg error text-right mt-2">
            {{ errors[0] }}
          </p>
        </ValidationProvider>
      </div>
    </div>

    <div class="flex items-baseline -mx-2 group justify-between">
      <div class="flex-none px-2 uppercase">
        <label for="orderSupplier">προμηθευτης:</label>
      </div>
      <div class="w-1/3 px-2">
        <ValidationProvider
          name="supplier"
          vid="supplierUuid"
          rules="required"
          v-slot="{ errors, failed }"
          tag="div"
        >
          <VSelect v-model="orderSupplier" :options="supplierOptions" class="vue-select" />
          <p v-if="failed" class="msg error text-right mt-2">
            {{ errors[0] }}
          </p>
        </ValidationProvider>
      </div>
    </div>

    <div
      v-if="formData.deliveryAt !== undefined"
      class="flex items-baseline -mx-2 group justify-between"
    >
      <div class="flex-none px-2 uppercase">
        <label for="orderDeliveryAt">παραδοση:</label>
      </div>
      <div class="w-1/3 px-2">
        <ValidationProvider name="delivery" vid="deliveryAt" v-slot="{ errors, failed }" tag="div">
          <input
            id="orderDeliveryAt"
            type="date"
            v-model.lazy="orderDeliveryAt"
            :readonly="!$membershipHasPermissionTo('edit order items')"
            :disabled="submitting"
            :class="{
              'bg-transparent': !failed,
              'border-transparent': !failed,
              'focus:bg-gray-400': !failed,
              'group-hover:border-gray-400': !failed,
              'bg-red-200': failed,
              'text-red-700': failed,
              'border-red-200': failed,
              'group-hover:border-red-500': failed,
              'focus:bg-red-500': failed,
              'focus:border-red-500': failed,
              'focus:text-white': failed,
            }"
            class="text-right w-full rounded px-4 py-2 leading-normal border outline-none bg-gray-200"
            placeholder="delivery date"
          />
          <p v-if="failed" class="msg error text-right mt-2">
            {{ errors[0] }}
          </p>
        </ValidationProvider>
      </div>
    </div>

    <div class="flex items-top -mx-2 group justify-between">
      <div class="flex-none px-2 uppercase">
        <label for="orderDetails">σημειωσεις:</label>
      </div>
      <div class="w-2/3 px-2">
        <textarea
          type="text"
          id="orderDetails"
          class="block w-full rounded px-4 py-2 leading-normal border outline-none bg-gray-200"
          :class="{
            'bg-transparent': true,
            'border-transparent': true,
            'focus:bg-gray-400': true,
            'group-hover:border-gray-400': true,
          }"
          :readonly="!$membershipHasPermissionTo('edit order items')"
          :disabled="submitting"
          placeholder="e.g. John Smith Advisor"
          v-model.lazy="orderDetails"
          :rows="noteRows"
        />
        <div class="flex mt-2">
          <button class="ml-auto btn btn-brand capitalize" @click="toggleRows">
            {{ noteRows === 3 ? 'expand' : 'collapse' }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="formData.assigneeUuid !== undefined"
      class="flex items-baseline -mx-2 group justify-between"
    >
      <div class="flex-none px-2 uppercase">
        <label for="orderAssigneeUuid">αναθεση:</label>
      </div>
      <div class="w-1/3 px-2">
        <ValidationProvider
          name="assignee"
          vid="assigneeUuid"
          rules="required"
          v-slot="{ errors, failed }"
          tag="div"
        >
          <div class="select-wrapper">
            <select
              v-model.lazy="orderAssigneeUuid"
              id="orderAssigneeUuid"
              :disabled="submitting"
              :class="{ 'has-error': failed }"
              class="select capitalize"
            >
              <option
                v-for="assignee in assignees"
                :key="assignee.id"
                :value="assignee.id"
                class="capitalize"
              >
                {{ assignee.firstName }} {{ assignee.lastName }}
              </option>
            </select>
          </div>
          <p v-if="failed" class="msg error text-right mt-2">
            {{ errors[0] }}
          </p>
        </ValidationProvider>
      </div>
    </div>

    <div
      v-if="formData.contactPointUserId !== undefined"
      class="flex items-baseline -mx-2 group justify-between"
    >
      <div class="flex-none px-2 uppercase">
        <label for="orderContactPointUserId">contact point:</label>
      </div>
      <div class="w-1/3 px-2">
        <ValidationProvider
          name="contactPointUserId"
          vid="contactPointUserId"
          v-slot="{ errors, failed }"
          tag="div"
        >
          <div class="select-wrapper">
            <select
              v-model.lazy="orderContactPointUserId"
              id="orderContactPointUserId"
              :disabled="submitting"
              :class="{ 'has-error': failed }"
              class="select capitalize"
            >
              <option
                v-for="contactPointUser in contactPointUsers"
                :key="contactPointUser.id"
                :value="contactPointUser.id"
                class="capitalize"
              >
                {{ contactPointUser.firstName }} {{ contactPointUser.lastName }}
              </option>
            </select>
          </div>
          <p v-if="failed" class="msg error text-right mt-2">
            {{ errors[0] }}
          </p>
        </ValidationProvider>
      </div>
    </div>

    <div
      v-if="formData.statusId !== undefined"
      class="flex items-baseline -mx-2 group justify-between"
    >
      <div class="flex-none px-2 uppercase">
        <label for="orderStatus">κατασταση:</label>
      </div>
      <div class="w-1/3 px-2">
        <ValidationProvider
          name="status"
          vid="statusId"
          rules="required"
          v-slot="{ errors, failed }"
          tag="div"
        >
          <div class="select-wrapper">
            <select
              v-model.lazy="orderStatusId"
              id="orderStatus"
              :disabled="submitting"
              :class="{ 'has-error': failed }"
              class="select capitalize"
            >
              <option
                v-for="status in statuses"
                :key="status.id"
                :value="status.id"
                class="capitalize"
              >
                {{ status.name }}
              </option>
            </select>
          </div>
          <p v-if="failed" class="msg error text-right mt-2">
            {{ errors[0] }}
          </p>
        </ValidationProvider>
      </div>
    </div>

    <div class="flex items-baseline -mx-2 group justify-between">
      <div class="flex-none px-2 uppercase">
        <label for="orderStatus">κατασταση Παραγωγής:</label>
      </div>
      <div class="w-1/3 px-2 capitalize text-right">
        {{ formData.order_status || 'Not Set' }}
      </div>
    </div>

    <div v-if="formData.tags !== undefined" class="flex items-baseline -mx-2 group justify-between">
      <div class="flex-none px-2 uppercase">
        <label for="orderStatus">tags:</label>
      </div>
      <div class="w-2/3 px-2">
        <ul class="-m-1 flex flex-wrap justify-end">
          <li v-for="tag in tags" :key="tag.id" class="inline-flex p-1">
            <label class="relative">
              <input
                v-model="orderTags"
                :value="tag.id"
                type="checkbox"
                class="field opacity-0 absolute inset-0 w-full h-full"
              />
              <span
                class="inline-block px-4 py-1 text-sm rounded-full bg-gray-300 leading-6 field-checked:bg-blue-600 field-checked:text-white transition-colors duration-75"
              >
                {{ tag.name }}
              </span>
            </label>
          </li>
        </ul>
      </div>
    </div>

    <div v-if="formData.priorityHigh !== undefined" class="flex items-baseline -mx-2 justify-end">
      <div class="flex-none px-2 text-right">
        <ValidationProvider
          name="priority"
          vid="orderPriorityHigh"
          v-slot="{ errors, failed }"
          tag="div"
        >
          <label class="custom-checkbox">
            <input
              v-model="orderPriorityHigh"
              id="orderPriority"
              type="checkbox"
              name="orderPriorityHigh"
              :disabled="submitting"
            />
            <span />
            <span class="capitalize">προτεραιότητα υψηλή</span>
          </label>
          <p v-if="failed" class="msg error text-right mt-2">
            {{ errors[0] }}
          </p>
        </ValidationProvider>
      </div>
    </div>

    <div v-if="formData.published !== undefined" class="flex items-baseline -mx-2 justify-end">
      <div class="flex-none px-2 text-right">
        <ValidationProvider name="published" vid="published" rules="required" tag="div">
          <label class="custom-checkbox">
            <input
              v-model="orderPublished"
              id="orderPublishedAt"
              type="checkbox"
              name="orderPublishedAt"
              :disabled="submitting"
            />
            <span />
            <span class="capitalize">published</span>
          </label>
        </ValidationProvider>
      </div>
    </div>
  </form>
</template>

<script>
export default {
  name: 'CreateForm',
  props: {
    submitting: Boolean,
    formData: {
      type: Object,
      required: true,
    },
    statuses: {
      type: Array,
      default: () => [],
    },
    tags: {
      type: Array,
      default: () => [],
    },
    assignees: {
      type: Array,
      default: () => [],
    },
    contactPointUsers: {
      type: Array,
      default: () => [],
    },
    supplierOptions: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    noteRows: 3,
  }),
  computed: {
    orderTags: {
      get() {
        return this.formData.tags;
      },
      set(tags) {
        this.$emit('changed', { tags });
      },
    },
    orderName: {
      get() {
        return this.formData.name;
      },
      set(name) {
        this.$emit('changed', { name });
      },
    },
    orderDeliveryAt: {
      get() {
        return this.formData.deliveryAt;
      },
      set(deliveryAt) {
        this.$emit('changed', { deliveryAt });
      },
    },
    orderPriorityHigh: {
      get() {
        return this.formData.priorityHigh;
      },
      set(priorityHigh) {
        this.$emit('changed', { priorityHigh });
      },
    },
    orderPublished: {
      get() {
        return this.formData.published;
      },
      set(published) {
        this.$emit('changed', { published });
      },
    },
    orderStatusId: {
      get() {
        return this.formData.statusId;
      },
      set(statusId) {
        this.$emit('changed', { statusId });
      },
    },
    orderDetails: {
      get() {
        return this.formData.details;
      },
      set(details) {
        this.$emit('changed', { details });
      },
    },
    orderSupplier: {
      get() {
        if (!this.formData.supplierUuid) return null;
        return (
          this.supplierOptions.find((option) => option.value === this.formData.supplierUuid) || null
        );
      },
      set(supplier) {
        const { value: supplierUuid = null, accountRelationUuid: clientUuid = null } =
          supplier || {};
        this.$emit('changed', { supplierUuid, clientUuid, projectUuid: null });
      },
    },
    orderAssigneeUuid: {
      get() {
        return this.formData.assigneeUuid;
      },
      set(assigneeUuid) {
        this.$emit('changed', { assigneeUuid });
      },
    },
    orderContactPointUserId: {
      get() {
        return this.formData.contactPointUserId;
      },
      set(contactPointUserId) {
        this.$emit('changed', { contactPointUserId });
      },
    },
    suppliers() {
      return this.$store.getters['ui/organizations/organizations'];
    },
  },
  methods: {
    toggleRows() {
      this.noteRows = this.noteRows === 3 ? 20 : 3;
    },
  },
};
</script>
